import { Box, Stack, Alert, CircularProgress } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router";
import { useGetOneOrganization } from "src/api/client-api/company/useGetOneOrganization.ts";
import { useCurrentSubscription } from "src/api/client-api/payment/useCurrentSubscription";
import { usePaymentPlans } from "src/api/client-api/payment/usePaymentPlans";
import { useApplicationParameters } from "src/api/client-api/appParams/useApplicationParameters";
import { usePortalSession } from "src/api/client-api/payment/usePortalSession";
import { useResolvedPaymentPlan } from "../hooks/useResolvedPaymentPlan";
import { useCompanyValidation } from "../hooks/useCompanyValidation";
import { useSubscriptionFormatting } from "../hooks/useSubscriptionFormatting";
import PaymentNoActiveSubscriptionAlert from "./PaymentNoActiveSubscriptionAlert";
import { CurrentSubscriptionSection } from "./CurrentSubscriptionSection";
import { PricingTableSection } from "./PricingTableSection";
import { useFoodpilotContext, MODIFY_ORG_SETTINGS, ORGANIZATION } from "src/context/FoodpilotContext";

export const PaymentSubscriptionSection = () => {
    const theme = useTheme();
    const { t } = useTranslation();
    const location = useLocation();
    const { selectedCompany, canUser } = useFoodpilotContext();
    const { isFeatureActivated } = useApplicationParameters();
    const isPaymentEnabled = isFeatureActivated("FS-payment-enabled");
    const companyId = selectedCompany?.id?.toString();
    const paymentCompanyId = isPaymentEnabled ? companyId : undefined;

    const { company, isLoading: companyLoading } = useGetOneOrganization({
        companyId: paymentCompanyId,
        withoutRelations: true,
    });
    const { data: subscription, isLoading: subscriptionLoading } = useCurrentSubscription({
        isPaymentFeatureEnabled: isPaymentEnabled,
    });
    const { data: plans, isLoading: plansLoading } = usePaymentPlans({
        isPaymentFeatureEnabled: isPaymentEnabled,
    });

    const returnUrl = `${window.location.origin}${location.pathname}${location.search}${location.hash}`;
    const { openPortal, loading: portalLoading } = usePortalSession({
        returnUrl,
        isPaymentFeatureEnabled: isPaymentEnabled,
    });

    const { resolvedPlan } = useResolvedPaymentPlan(subscription ?? undefined, plans);
    const { requiredMissing } = useCompanyValidation(company);
    const { formattedAmount, intervalLabel } = useSubscriptionFormatting(resolvedPlan);
    const canModify = canUser(MODIFY_ORG_SETTINGS, ORGANIZATION);

    if (!isPaymentEnabled) {
        return (
            <Alert severity="info">
                {t(
                    "companySettings.payment.feature_disabled",
                    "Payment feature is not enabled. Contact your administrator.",
                )}
            </Alert>
        );
    }

    if (subscriptionLoading || plansLoading || companyLoading) {
        return (
            <Box display="flex" justifyContent="center" p={4}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Stack gap={theme.spacing(4)}>
            {requiredMissing && (
                <Alert severity="info">
                    {t(
                        "companySettings.subscription.missing_required",
                        "Please complete required information (Company name, Email, Country, Zip code) in the General page before subscribing.",
                    )}
                </Alert>
            )}

            {/* No Subscription Banner */}
            {!requiredMissing && !subscription && <PaymentNoActiveSubscriptionAlert />}

            {/* Current Subscription Section */}
            {!requiredMissing && subscription && (
                <CurrentSubscriptionSection
                    subscription={subscription}
                    resolvedPlan={resolvedPlan ?? null}
                    formattedAmount={formattedAmount}
                    intervalLabel={intervalLabel}
                    company={company ?? null}
                    onOpenPortal={openPortal}
                    canModify={canModify}
                    portalLoading={portalLoading}
                />
            )}

            {/* Pricing Table Section - only when user can modify (subscribe/change) */}
            {!requiredMissing && canModify && <PricingTableSection />}
        </Stack>
    );
};
